/**
 * The three things a document can count as, and the one-click control
 * that picks between them.
 *
 * Shared by the document's row ("Counts as", saved on click) and the
 * review popup ("This document is", saved with the confirm). Its own
 * file so both import one copy of the words and hover text rather than
 * the popup reaching into the row component.
 */
import { useId } from 'react'
import type { DocumentKind, ReferenceDoc } from '@/types'

/** In the order the control shows them. */
export const DOCUMENT_KIND_ORDER: readonly DocumentKind[] = ['requirements', 'defects', 'reference']
export const DOCUMENT_KIND_LABEL: Record<DocumentKind, string> = {
  requirements: 'Requirements',
  defects: 'Defects',
  reference: 'Reference',
}
export const DOCUMENT_KIND_HINT: Record<DocumentKind, string> = {
  requirements: 'Its parts are things the app must do. Each counts toward Requirements covered.',
  defects: 'Its parts are bugs that must not come back. Each counts toward Defects covered.',
  reference:
    'It describes how the app works. Its proposed tests are written and run, but it adds no requirements or defects.',
}

/** The note beside the control, or null for no note. The server
 * resolves which kind is selected and who decided it; this only words
 * it. */
export function countsAsNote(doc: ReferenceDoc): string | null {
  if (doc.kindSource === 'user') return 'You chose this.'
  if (doc.kindSource === 'suggested') {
    return doc.suggestedKindReason
      ? `Suggested by OttoTester: ${doc.suggestedKindReason}`
      : 'Suggested by OttoTester.'
  }
  return null
}

/** A segmented control of the three kinds. It only reports clicks; the
 * caller decides whether a click saves at once or waits for a confirm. */
export function DocumentKindControl({
  label,
  groupName,
  selected,
  note,
  busy,
  onPick,
  className = 'mt-1.5 text-[11px]',
}: {
  /** The words before the control: "Counts as", "This document is". */
  label: string
  /** The group's accessible name, e.g. "What spec.pdf counts as". */
  groupName: string
  selected: DocumentKind
  note: string | null
  busy: boolean
  onPick: (kind: DocumentKind) => void
  className?: string
}) {
  // The hover text is also tied to each option as its description, so a
  // keyboard or screen-reader user hears it on focus, not only a mouse
  // user on hover.
  const hintIdBase = useId()
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground ${className}`}>
      <span>{label}</span>
      <span role="group" aria-label={groupName} className="inline-flex overflow-hidden rounded-md border border-border">
        {DOCUMENT_KIND_ORDER.map(kind => {
          const on = kind === selected
          return (
            <button
              key={kind}
              type="button"
              title={DOCUMENT_KIND_HINT[kind]}
              aria-describedby={`${hintIdBase}-${kind}`}
              aria-pressed={on}
              disabled={busy}
              onClick={() => onPick(kind)}
              className={
                on
                  ? 'bg-primary/10 px-2 py-0.5 font-semibold text-primary disabled:opacity-60'
                  : 'bg-background px-2 py-0.5 text-muted-foreground hover:text-foreground disabled:opacity-60'
              }
            >
              {DOCUMENT_KIND_LABEL[kind]}
            </button>
          )
        })}
      </span>
      {/* Outside the buttons so the hint describes each option without
          becoming part of its name. */}
      {DOCUMENT_KIND_ORDER.map(kind => (
        <span key={kind} id={`${hintIdBase}-${kind}`} className="sr-only">
          {DOCUMENT_KIND_HINT[kind]}
        </span>
      ))}
      {note && <span>{note}</span>}
    </div>
  )
}
